import * as qs from 'qs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { errorMessagesListFromResponse } from '../../../../../helpers/errors';
import useAsyncRequest from '../../../../../hooks/useAsyncRequest';
import useDebouncedCallback from '../../../../../hooks/useDebouncedCallback';
import { get, post, put, remove } from '../../../../../redux/api';
import type { NewPortalToken, PortalToken } from '../../../../../../shared/publicPortal/types';
import type { FailedFetchResponse } from '../../../../../helpers/errors';

const useDeletePortalToken = ({
  endpoint,
  tokens,
  setTokens,
}: {
  endpoint: string;
  tokens: PortalToken[] | null;
  setTokens: (tokens: PortalToken[]) => void;
}) => {
  const addToast = useAddToast();
  const onDeleteToken = useCallback(
    async (id: number) => {
      try {
        await remove<{ ids: number[] }, void>(endpoint, {
          body: {
            ids: [id],
          },
          json: true,
        });
        setTokens(tokens?.filter((token) => token.id !== id) || []);
        addToast({ type: 'success', content: 'Portal token deleted' });
      } catch (error: unknown) {
        addToast({ type: 'error', content: 'Failed to delete portal token' });
      }
    },
    [addToast, endpoint, setTokens, tokens],
  );

  return {
    onDeleteToken,
  };
};

const useEditPortalToken = ({
  endpoint,
  tokens,
  setTokens,
}: {
  endpoint: string;
  tokens: PortalToken[] | null;
  setTokens: (tokens: PortalToken[]) => void;
}) => {
  const addToast = useAddToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editErrors, setEditErrors] = useState<string[] | null>(null);
  const onEditToken = useCallback(
    async (id: number, newPortalToken: NewPortalToken) => {
      try {
        setIsEditing(true);
        setEditErrors([]);
        await put<NewPortalToken, void>(`${endpoint}/${id}`, newPortalToken);
        const updatedTokens = tokens?.map((token) =>
          token.id === id ? ({ ...token, ...newPortalToken } as PortalToken) : token,
        );
        setTokens(updatedTokens || []);
        addToast({ type: 'success', content: 'Portal token updated' });
      } catch (error: unknown) {
        const errors = errorMessagesListFromResponse(error as FailedFetchResponse);
        setEditErrors(errors);
        throw error;
      } finally {
        setIsEditing(false);
      }
    },
    [endpoint, addToast, tokens, setTokens, setEditErrors],
  );

  return {
    isEditing,
    editErrors,
    onEditToken,
    onResetEditErrors: () => setEditErrors(null),
  };
};

const useCreatePortalToken = ({
  endpoint,
  tokens,
  setTokens,
}: {
  endpoint: string;
  tokens: PortalToken[] | null;
  setTokens: (tokens: PortalToken[]) => void;
}) => {
  const addToast = useAddToast();
  const [isCreating, setIsCreating] = useState(false);
  const [createErrors, setMutateErrors] = useState<string[] | null>(null);
  const onCreateToken = useCallback(
    async (newPortalToken: NewPortalToken) => {
      try {
        setIsCreating(true);
        setMutateErrors([]);
        const { data } = await post<NewPortalToken[], PortalToken[]>(endpoint, [newPortalToken]);
        const created = data?.[0] as PortalToken;
        const tokenText = data?.at(0)?.token || null;
        if (!tokenText) {
          setMutateErrors(['Error creating token.']);
        }
        setTokens([...(tokens || []), created]);
        addToast({ type: 'success', content: 'Portal token created' });
        return tokenText;
      } catch (error: unknown) {
        const errors = errorMessagesListFromResponse(error as FailedFetchResponse);
        setMutateErrors(errors);
      } finally {
        setIsCreating(false);
      }

      return null;
    },
    [endpoint, addToast, tokens, setTokens, setMutateErrors],
  );

  return {
    isCreating,
    createErrors,
    onCreateToken,
    onResetCreateErrors: () => setMutateErrors(null),
  };
};

const usePortalTokenSettings = (organizationId: number) => {
  // Read
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState<string | undefined>();
  const debouncedOnSearchChange = useDebouncedCallback(setDebouncedSearch);
  const query = qs.stringify({ search: debouncedSearch });

  const endpoint = useMemo(
    () => `/api/organizations/${organizationId}/settings/portal/tokens${query ? `?${query}` : ''}`,
    [organizationId, query],
  );

  const fetchTokens = useCallback(() => get<PortalToken[]>(endpoint), [endpoint]);
  const { value: initialTokens, isLoading: isFetchLoading, errors: fetchErrors } = useAsyncRequest(fetchTokens);

  const [tokens, setTokens] = useState(initialTokens);
  useEffect(() => {
    if (initialTokens) {
      setTokens(initialTokens);
    }
  }, [initialTokens]);

  // Create
  const { createErrors, isCreating, onCreateToken, onResetCreateErrors } = useCreatePortalToken({
    endpoint,
    tokens,
    setTokens,
  });

  // Edit
  const { isEditing, editErrors, onEditToken, onResetEditErrors } = useEditPortalToken({ endpoint, tokens, setTokens });

  // Delete
  const { onDeleteToken } = useDeletePortalToken({ endpoint, tokens, setTokens });

  return {
    settings: tokens,
    errors: fetchErrors || createErrors ? [...(fetchErrors || []), ...(createErrors || [])] : null,
    isLoading: {
      fetch: isFetchLoading,
      save: isCreating,
      any: isFetchLoading || isCreating,
    },
    setSearch: (input: string) => {
      setSearch(input);
      debouncedOnSearchChange(input);
    },
    search,
    onCreateToken,
    isCreating,
    createErrors,
    onResetCreateErrors,
    isEditing,
    onEditToken,
    editErrors,
    onResetEditErrors,
    onDeleteToken,
  };
};

export default usePortalTokenSettings;
